// qboService.js
// API service for QuickBooks Online integration

import { HTTPService } from './httpService';

const API = new HTTPService();

// ============================================================================
// Connection Management
// ============================================================================

/**
 * Get QBO connection status for an installer
 * @param {number} installerId
 * @returns {{ enabled, connected, status, last_sync_at, connected_at }}
 */
export const getConnection = async (installerId) => {
    const { data } = await API.get(`/qbo/connection/${installerId}`);
    return data.data || data;
};

/**
 * Enable QBO feature for an installer
 * @param {number} installerId
 */
export const enable = async (installerId) => {
    const { data } = await API.post(`/qbo/enable/${installerId}`);
    return data;
};

/**
 * Disable QBO feature for an installer
 * @param {number} installerId
 */
export const disable = async (installerId) => {
    const { data } = await API.post(`/qbo/disable/${installerId}`);
    return data;
};

// ============================================================================
// OAuth
// ============================================================================

/**
 * Get QBO OAuth authorization URI
 * @param {number} installerId
 * @returns {{ authUri }}
 */
export const getAuthUri = async (installerId) => {
    const { data } = await API.get(`/qbo/auth/${installerId}`);
    return data.data || data;
};

/**
 * Disconnect QBO (revoke access)
 * @param {number} installerId
 */
export const disconnect = async (installerId) => {
    const { data } = await API.post(`/qbo/disconnect/${installerId}`);
    return data;
};

// ============================================================================
// Sync Operations
// ============================================================================

/**
 * Sync a single payment record to QBO
 * @param {number} installerPaymentId
 * @returns {{ success, queued }}
 */
export const syncPayment = async (installerPaymentId) => {
    const { data } = await API.post(`/qbo/sync/payment/${installerPaymentId}`);
    return data;
};

/**
 * Bulk sync multiple payment records to QBO
 * @param {number[]} paymentIds
 * @returns {{ success, queued, total }}
 */
export const syncBulk = async (paymentIds) => {
    const { data } = await API.post('/qbo/sync/bulk', { paymentIds });
    return data;
};

// ============================================================================
// Admin Setup (Phase 2)
// ============================================================================

/**
 * Get QBO chart of accounts for mapping
 * @param {number} installerId
 * @returns {{ accounts, mappings, missing }}
 */
export const getSetupAccounts = async (installerId) => {
    const { data } = await API.get(`/qbo/setup/accounts/${installerId}`);
    return data.data || data;
};

/**
 * Save QBO account mappings
 * @param {number} installerId
 * @param {object} mappings - account mapping configuration
 */
export const saveSetupAccounts = async (installerId, mappings) => {
    const { data } = await API.post(`/qbo/setup/accounts/${installerId}`, mappings);
    return data;
};

/**
 * Activate QBO integration (go live)
 * @param {number} installerId
 */
export const activate = async (installerId) => {
    const { data } = await API.post(`/qbo/setup/activate/${installerId}`);
    return data;
};

/**
 * Get QBO sync health status
 * @param {number} installerId
 * @returns {{ status, lastSync, errorCount, pendingCount }}
 */
export const getHealth = async (installerId) => {
    const { data } = await API.get(`/qbo/health/${installerId}`);
    return data.data || data;
};

/**
 * Get QBO sync log history
 * @param {number} installerId
 * @returns {Array} sync log entries
 */
export const getSyncLog = async (installerId) => {
    const { data } = await API.get(`/qbo/sync-log/${installerId}`);
    return data.data || data;
};

/**
 * Retry failed QBO syncs
 * @param {number} installerId
 * @returns {{ success, retried }}
 */
export const retryFailed = async (installerId) => {
    const { data } = await API.post(`/qbo/retry/${installerId}`);
    return data;
};

// Default export for convenience
const QboAPI = {
    getConnection,
    enable,
    disable,
    getAuthUri,
    disconnect,
    syncPayment,
    syncBulk,
    getSetupAccounts,
    saveSetupAccounts,
    activate,
    getHealth,
    getSyncLog,
    retryFailed
};

export default QboAPI;
